import { ReactNode, useEffect, useRef, useState } from "react";
import { usePlaySound } from "common/utils";
import { PlanCategory, PlanType, BillingPeriod } from "features/subscription";
import { UserPlanComparisonCard } from "./UserPlanComparisonCard";

const planTypeToCategory = (plan: PlanType): PlanCategory | undefined => {
  if (plan === PlanType.STANDARD) return "standard";
  if (plan === PlanType.PREMIUM) return "premium";
  return undefined;
};

const buildDisplayPlans = (currentPlan: PlanType, includeFreePlan: boolean): PlanType[] => {
  if (currentPlan === PlanType.TRIAL) {
    return includeFreePlan
      ? [PlanType.TRIAL, PlanType.FREE, PlanType.STANDARD, PlanType.PREMIUM]
      : [PlanType.TRIAL, PlanType.STANDARD, PlanType.PREMIUM];
  }
  return includeFreePlan
    ? [PlanType.FREE, PlanType.STANDARD, PlanType.PREMIUM]
    : [PlanType.STANDARD, PlanType.PREMIUM];
};

const CARD_WIDTH = 384; // w-96
const CARD_GAP = 12; // gap-3

type Props = {
  currentPlan: PlanType;
  availablePlans: PlanCategory[];
  billingPeriod: BillingPeriod;
  getPriceString: (category: PlanCategory, billingPeriod: BillingPeriod) => string;
  onChangeBillingPeriod: (period: BillingPeriod) => void;
  onUpgrade: (category: PlanCategory) => void;
  includeFreePlan?: boolean;
  leadingCard?: ReactNode;
};

export const UserPlanUpgradeCta = ({
  currentPlan,
  availablePlans,
  billingPeriod,
  getPriceString,
  onChangeBillingPeriod,
  onUpgrade,
  includeFreePlan = false,
  leadingCard,
}: Props) => {
  const play = usePlaySound();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrolling, setScrolling] = useState(false);

  const displayPlans = buildDisplayPlans(currentPlan, includeFreePlan);

  const updateScrollState = (element: HTMLDivElement) => {
    const maxScroll = element.scrollWidth - element.clientWidth;
    setCanScrollLeft(element.scrollLeft > 1);
    setCanScrollRight(element.scrollLeft < maxScroll - 1);
  };

  useEffect(() => {
    if (scrollRef.current) updateScrollState(scrollRef.current);
  }, [displayPlans.length]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const handleScrollEnd = () => {
      updateScrollState(element);
      setScrolling(false);
    };

    const supportsScrollEnd = "onscrollend" in window;
    if (supportsScrollEnd) {
      element.addEventListener("scrollend", handleScrollEnd);
      return () => element.removeEventListener("scrollend", handleScrollEnd);
    }

    let scrollEndTimer: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(handleScrollEnd, 150);
    };
    element.addEventListener("scroll", handleScroll);
    return () => {
      element.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollEndTimer);
    };
  }, []);

  const handleScrollBtn = (direction: number) => () => {
    if (scrolling || !scrollRef.current) return;
    play();
    setScrolling(true);
    scrollRef.current.scrollBy({
      left: direction * (CARD_WIDTH + CARD_GAP),
      behavior: "smooth",
    });
  };

  const isPurchasable = (plan: PlanType) => {
    const category = planTypeToCategory(plan);
    return !!category && availablePlans.includes(category);
  };

  return (
    <div className="flex-col-el flex-1 min-h-0 gap-3 overflow-hidden">
      <div className="flex-row-el flex-1 min-h-0 items-center gap-2">
        <button
          type="button"
          onClick={handleScrollBtn(-1)}
          disabled={!canScrollLeft}
          aria-label="scroll left"
          className={`type-inherit-font shrink-0 flex-row-el items-center justify-center w-12 h-12 rounded-full bg-white border-2 border-solid border-outline shadow-[0_3px_0_rgba(63,74,97,0.18),0_4px_10px_rgba(63,74,97,0.1)] active:translate-y-[2px] transition-transform cursor-pointer ${
            !canScrollLeft ? "opacity-30 cursor-not-allowed" : ""
          }`}
        >
          <span className="material-symbols-outlined type-title text-textcolor-black">
            chevron_left
          </span>
        </button>

        <div
          ref={scrollRef}
          className="flex-row-el flex-1 min-w-0 min-h-0 self-stretch items-stretch gap-3 px-12 scroll-px-12 overflow-x-auto snap-x invisible-scrollbar [mask-image:linear-gradient(to_right,transparent_0,black_48px,black_calc(100%-48px),transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0,black_48px,black_calc(100%-48px),transparent_100%)]"
        >
          {leadingCard && (
            <div className="flex-col-el shrink-0 w-96 snap-start min-h-0">{leadingCard}</div>
          )}
          {displayPlans.map((plan) => {
            const isCurrent = plan === currentPlan;
            const category = planTypeToCategory(plan);
            const purchasable = isPurchasable(plan);
            return (
              <div key={plan} className="flex-col-el shrink-0 w-96 snap-start min-h-0">
                <UserPlanComparisonCard
                  plan={plan}
                  isCurrent={isCurrent}
                  priceString={
                    !isCurrent && category && purchasable
                      ? getPriceString(category, billingPeriod)
                      : undefined
                  }
                  category={!isCurrent && purchasable ? category : undefined}
                  onUpgrade={!isCurrent && purchasable ? onUpgrade : undefined}
                  billingPeriod={billingPeriod}
                  onChangeBillingPeriod={onChangeBillingPeriod}
                />
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={handleScrollBtn(1)}
          disabled={!canScrollRight}
          aria-label="scroll right"
          className={`type-inherit-font shrink-0 flex-row-el items-center justify-center w-12 h-12 rounded-full bg-white border-2 border-solid border-outline shadow-[0_3px_0_rgba(63,74,97,0.18),0_4px_10px_rgba(63,74,97,0.1)] active:translate-y-[2px] transition-transform cursor-pointer ${
            !canScrollRight ? "opacity-30 cursor-not-allowed" : ""
          }`}
        >
          <span className="material-symbols-outlined type-title text-textcolor-black">
            chevron_right
          </span>
        </button>
      </div>
    </div>
  );
};
